import React from 'react';
import { useStore } from 'zustand';
import {
  TrendLine,
  InteractiveText,
  FibonacciRetracement,
  GannFan,
  FibChannel,
  ElliottImpulse,
  Measure,
  Rectangle,
  Triangle,
  FixedRangeVolumeProfile,
  AnchoredVolumeProfile,
  InteractiveOrderLine,
} from './InteractiveShapes';
import type { StockData } from '@/types/chart';
import { OhlcvIndex } from '@/types/chart';
import { ShapeType } from '@/types/commontypes';
import { MeasureBgColor } from '@/utils/findMatchingShape';
import { solidColor } from '@/utils/rgbTorgba';
import type {
  ChartId,
  DrawingSharedState,
  ShapeSelection,
  RenderNode,
  MoreProps,
  TransientBroadcastDragOverride,
  RFCInteractiveItem,
  OrderLineRenderNode,
} from '@/types/commontypes';
import { toRFCItem } from '@/types/commontypes';
import { useChartShapes } from '../../contexts';
import { useTheme } from '../../contexts/ThemeContext';
import { useChartUIContext } from '../../contexts/ChartUIContext';
import { useChartUIStoreSelector } from '../../stores/chartUIStore';
import { useGlobalShapeSelector } from '../../contexts/GlobalShapesContext';
import { VertLine } from './InteractiveShapes/InteractiveVerticalLine';
import { PathImpulse } from './InteractiveShapes/PathImpulse';

/**
 * Injects `selected` from the global selection state into shape arrays before passing to RFC library components.
 * Returns RFCInteractiveItem[] (with index signature) for direct consumption by RFC components.
 */
function injectSelected(
  shapes: RenderNode[] | undefined,
  selectedGlobal: ShapeSelection | null,
  chartId: ChartId,
  type: ShapeType,
  timeframe?: string,
): RFCInteractiveItem[] | undefined {
  if (!shapes?.length) return undefined;
  const selectedUuid =
    selectedGlobal?.chartId === chartId &&
    selectedGlobal?.type === type &&
    (!timeframe || selectedGlobal?.timeframe === timeframe)
      ? selectedGlobal.uuid
      : null;
  return shapes.map(s => {
    const item = toRFCItem(s);
    item.selected = s.uuid === selectedUuid;
    return item;
  });
}

interface ShapeRenderComponentProps {
  onDrawComplete: (event: React.MouseEvent, newShapes: RenderNode[], type: ShapeType) => void;
  onDragComplete: (event: React.MouseEvent, newShapes: RenderNode[], type: ShapeType, dragged?: boolean) => void;
  onStart: (
    e: React.MouseEvent,
    xyValue: [number, number] | [number, number][],
    moreProps: MoreProps,
    type: ShapeType,
  ) => void;
  onChoosePostion: (e: React.MouseEvent, text: RenderNode[], moreProps: MoreProps) => void;
  onDragTextComplete: (e: React.MouseEvent, textList: RenderNode[], moreProps: MoreProps) => void;
  handleShapeRef: (ShapeType: ShapeType) => void;
  onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  handleDeleteAlert: (yCoordinate: OrderLineRenderNode, type: 'Entry' | 'TP' | 'SL') => void;
  handleDeleteAllOrderLines: () => void;
  chartId: ChartId;
  sharedState: DrawingSharedState;
  setSharedState: React.Dispatch<React.SetStateAction<DrawingSharedState>>;
  completePath?: boolean;
  isAnyShapeEnabled: boolean;
  hoverIds: Record<ChartId, string | null>;
  setHoverIds: React.Dispatch<React.SetStateAction<Record<ChartId, string | null>>>;
}

const ShapeRenderComponent: React.FC<ShapeRenderComponentProps> = ({
  onDrawComplete,
  onStart,
  onChoosePostion,
  onDragTextComplete,
  handleShapeRef,
  onDoubleClick,
  handleDeleteAlert,
  handleDeleteAllOrderLines,
  chartId,
  sharedState,
  setSharedState,
  onDragComplete,
  completePath,
  isAnyShapeEnabled,
  hoverIds,
  setHoverIds,
}) => {
  // Get state from contexts
  const shapeTypeToDraw = useGlobalShapeSelector(s => s.shapeTypeToDraw);
  const { shapeStore, drawState, timeframe } = useChartShapes();
  // per-chartId 셀렉터: 다른 chartId의 도형 변경에 반응하지 않는다.
  const chartShapes = useStore(shapeStore, s => s.renderNodes[chartId]);
  const commonShapes = useStore(shapeStore, s => s.renderNodes['chartId_common' as ChartId]);

  // Check if current chart is a candle chart (for FRVP restriction)
  const { chartUIStore, chartContainerRef } = useChartUIContext();
  const isCandleChart = useChartUIStoreSelector(
    chartUIStore,
    s => s.chartInfos.find(c => c.chartId === chartId)?.isCandleChart ?? false,
  );

  const selectedCanvasId = useGlobalShapeSelector(s => s.selectedCanvasId);
  const selectedShape = useGlobalShapeSelector(s => s.selectedShape);
  const setShapeJustSelected = useGlobalShapeSelector(s => s.setShapeJustSelected);
  const lastTPSLByDirection = useGlobalShapeSelector(s => s.lastTPSLByDirection);

  const yCoordinateClicked = useChartUIStoreSelector(chartUIStore, s => s.yCoordinateClicked);
  const setRatioDropdownRaw = useChartUIStoreSelector(chartUIStore, s => s.setRatioDropdown);
  const setRatioDropdown = React.useCallback(
    (data: Parameters<typeof setRatioDropdownRaw>[0]) => {
      if (data.position && chartContainerRef.current) {
        // mouseXY는 ChartCanvas SVG 기준 좌표. SVG 엘리먼트의 bounding rect로 변환한다.
        const svg = chartContainerRef.current.querySelector('svg');
        const rect = svg ? svg.getBoundingClientRect() : chartContainerRef.current.getBoundingClientRect();
        // eslint-disable-next-line no-param-reassign
        data = { ...data, position: { x: rect.left + data.position.x, y: rect.top + data.position.y } };
      }
      setRatioDropdownRaw(data);
    },
    [setRatioDropdownRaw, chartContainerRef],
  );

  // overrideData — transient channel 기반.
  // zustand set()을 거치지 않아 ShapeRender 및 다른 도형의 re-render가 없다.
  // VertLine이 chartUIStore.subscribeOverride()로 구독하여 자기만 forceUpdate.
  const handleOverride = React.useCallback(
    (data: TransientBroadcastDragOverride) => {
      chartUIStore.setTransientOverride(data);
    },
    [chartUIStore],
  );

  //Get hovered shape uuid to manage shape updation
  const handleHover = (id: string | number | null, moreProps: MoreProps) => {
    if (drawState.status === 'started') return;
    const currentChartId = moreProps?.chartId;
    if (!currentChartId) return;
    const strId = id != null ? String(id) : null;
    setHoverIds((prev: Record<ChartId, string | null>) => ({
      ...prev,
      [currentChartId]: strId,
    }));
  };

  const { isDarkMode } = useTheme();

  // Bind timeframe to injectSelected so callers don't need to pass it every time
  const inject = (shapes: RenderNode[] | undefined, cId: ChartId, sType: ShapeType) =>
    injectSelected(shapes, selectedShape, cId, sType, timeframe);

  const injectOrderLine = (shapes: OrderLineRenderNode[] | undefined, cId: ChartId): OrderLineRenderNode[] => {
    if (!shapes?.length) return [];
    const selectedUuid =
      selectedShape?.chartId === cId && selectedShape?.type === ShapeType.ORDER_LINE ? selectedShape.uuid : null;
    return shapes.map(s => ({ ...s, selected: s.uuid === selectedUuid }));
  };

  const renderShapeComponent = (type: ShapeType, enabled: boolean, props: Record<string, unknown>) => {
    const commonProps = {
      enabled,
      onStart: (event: React.MouseEvent, newShapes: unknown, moreProps: MoreProps) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        onStart(event, newShapes as [number, number] | [number, number][], moreProps, type),
      onComplete: (event: React.MouseEvent, newShapes: unknown[]) => {
        setShapeJustSelected(true);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        onDrawComplete(event, newShapes as RenderNode[], type);
      },
      onDragComplete: (event: React.MouseEvent, newShapes: unknown[]) => {
        // 드래그 시작 시 onSelect→handleMouseDown에서 이미 shapeJustSelected를 소비했으므로
        // dragComplete에서 다시 설정하지 않는다. 설정하면 다음 배경 mousedown에서 해제가 1회 지연됨.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        onDragComplete(event, newShapes as RenderNode[], type);
      },
      onSelect: (event: React.MouseEvent, newShapes: unknown[]) => {
        if (shapeTypeToDraw != null) return; // Prevent selection if any shape is enabled for drawing
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const typedShapes = newShapes as RenderNode[];
        // Signal that a shape was selected during this mousedown event.
        // ChartSection.handleClick reads this flag to distinguish shape click from background click.
        setShapeJustSelected(true);
        // zustand's set is synchronous — no flushSync needed
        onDragComplete(event, typedShapes, type);
      },
      onHover: handleHover,
      onDoubleClick,
      hoverId: hoverIds[chartId],
      sharedState,
      setSharedState,
      chartId,
      isAnyShapeEnabled,
      // TrendLineProps requires yCoordinateClicked (used only for HORZ_LINE; undefined is acceptable for others)
      yCoordinateClicked,
    };
    // Shared helpers to avoid repetition
    // snapTo returns number[] but library expects (datum: unknown) => number | number[]
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const snapToHl = ((d: StockData) => [d.ohlcv[OhlcvIndex.HIGH], d.ohlcv[OhlcvIndex.LOW]]) as unknown as (
      datum: unknown,
    ) => number | number[];

    // Cast class components to generic prop types so per-prop type mismatches (between
    // commonProps and the strict component prop interfaces) are handled by runtime checks
    // rather than rejected by TypeScript's structural checker.
    // "as unknown as React.ComponentType<Record<string, unknown>>" avoids `any`.
    type GenericComp = React.ComponentType<Record<string, unknown>>;
    /* eslint-disable @typescript-eslint/no-unsafe-type-assertion */
    const TrendLineC = TrendLine as unknown as GenericComp;
    const VertLineC = VertLine as unknown as GenericComp;
    const FibonacciRetracementC = FibonacciRetracement as unknown as GenericComp;
    const FibChannelC = FibChannel as unknown as GenericComp;
    const GannFanC = GannFan as unknown as GenericComp;
    const InteractiveTextC = InteractiveText as unknown as GenericComp;
    const ElliottImpulseC = ElliottImpulse as unknown as GenericComp;
    const RectangleC = Rectangle as unknown as GenericComp;
    const FixedRangeVolumeProfileC = FixedRangeVolumeProfile as unknown as GenericComp;
    const AnchoredVolumeProfileC = AnchoredVolumeProfile as unknown as GenericComp;
    const TriangleC = Triangle as unknown as GenericComp;
    const PathImpulseC = PathImpulse as unknown as GenericComp;
    /* eslint-enable @typescript-eslint/no-unsafe-type-assertion */

    switch (type) {
      case ShapeType.TREND_LINE:
        return (
          <TrendLineC
            ref={handleShapeRef(ShapeType.TREND_LINE)}
            {...commonProps}
            type="LINE"
            snap={false}
            snapTo={snapToHl}
            trends={inject(chartShapes?.TrendLine, chartId, ShapeType.TREND_LINE)}
            timeFrame={selectedCanvasId?.timeframe}
            shapeType={ShapeType.TREND_LINE}
            {...props}
          />
        );
      case ShapeType.RAY:
        return (
          <TrendLineC
            ref={handleShapeRef(ShapeType.RAY)}
            {...commonProps}
            type="LINE"
            snap={false}
            snapTo={snapToHl}
            trends={inject(chartShapes?.Ray, chartId, ShapeType.RAY)}
            timeFrame={selectedCanvasId?.timeframe}
            shapeType={ShapeType.RAY}
            {...props}
          />
        );
      case ShapeType.INFO_LINE:
        return (
          <TrendLineC
            ref={handleShapeRef(ShapeType.INFO_LINE)}
            {...commonProps}
            type="LINE"
            snap={false}
            snapTo={snapToHl}
            trends={inject(chartShapes?.InfoLine, chartId, ShapeType.INFO_LINE)}
            timeFrame={selectedCanvasId?.timeframe}
            shapeType={ShapeType.INFO_LINE}
            {...props}
          />
        );
      case ShapeType.HORZ_LINE:
        return (
          <TrendLineC
            ref={handleShapeRef(ShapeType.HORZ_LINE)}
            {...commonProps}
            type="XLINE"
            snap={false}
            snapTo={snapToHl}
            trends={inject(chartShapes?.HorzLine, chartId, ShapeType.HORZ_LINE)}
            yCoordinateClicked={yCoordinateClicked}
            direction="Horz"
            chartId={chartId}
            shapeType={ShapeType.HORZ_LINE}
            {...props}
          />
        );
      case ShapeType.VERT_LINE:
        return (
          <VertLineC
            ref={handleShapeRef(ShapeType.VERT_LINE)}
            {...commonProps}
            chartId={'chartId_common'}
            type="XLINE"
            snap={false}
            snapTo={snapToHl}
            trends={inject(commonShapes?.VertLine, 'chartId_common', ShapeType.VERT_LINE)}
            onOverride={handleOverride}
            chartUIStore={chartUIStore}
            direction="Vert"
            shapeType={ShapeType.VERT_LINE}
            {...props}
          />
        );
      case ShapeType.FIB_RETRACEMENT:
        return (
          <FibonacciRetracementC
            ref={handleShapeRef(ShapeType.FIB_RETRACEMENT)}
            {...commonProps}
            {...props}
            type="BOUND"
            retracements={inject(chartShapes?.FibRetracement, chartId, ShapeType.FIB_RETRACEMENT)}
          />
        );
      case ShapeType.FIB_CHANNEL:
        return (
          <FibChannelC
            ref={handleShapeRef(ShapeType.FIB_CHANNEL)}
            {...commonProps}
            {...props}
            type={ShapeType.FIB_CHANNEL}
            channels={inject(chartShapes?.FibChannel, chartId, ShapeType.FIB_CHANNEL)}
          />
        );
      case ShapeType.GANN_BOX:
        return (
          <GannFanC
            ref={handleShapeRef(ShapeType.GANN_BOX)}
            {...commonProps}
            {...props}
            type={ShapeType.GANN_BOX}
            fans={inject(chartShapes?.GannBox, chartId, ShapeType.GANN_BOX)}
            isGannBox={true}
          />
        );
      case ShapeType.GANN_FAN:
        return (
          <GannFanC
            ref={handleShapeRef(ShapeType.GANN_FAN)}
            {...commonProps}
            {...props}
            type={ShapeType.GANN_FAN}
            fans={inject(chartShapes?.GannFan, chartId, ShapeType.GANN_FAN)}
          />
        );
      case ShapeType.TEXT:
        return (
          <InteractiveTextC
            ref={handleShapeRef(ShapeType.TEXT)}
            onChoosePosition={(e: React.MouseEvent, textList: RenderNode[], moreProps: MoreProps) => {
              setShapeJustSelected(true);
              onChoosePostion(e, textList, moreProps);
            }}
            enabled={shapeTypeToDraw === ShapeType.TEXT}
            onDragComplete={(e: React.MouseEvent, textList: unknown[], moreProps: MoreProps) => {
              setShapeJustSelected(true);
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              onDragTextComplete(e, textList as RenderNode[], moreProps);
            }}
            onSelect={(e: React.MouseEvent, textList: unknown[], moreProps: MoreProps) => {
              setShapeJustSelected(true);
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              onDragTextComplete(e, textList as RenderNode[], moreProps);
            }}
            onDoubleClick={onDoubleClick}
            textList={inject(chartShapes?.[ShapeType.TEXT], chartId, ShapeType.TEXT) ?? []}
            defaultText={{ text: 'Enter Text' }}
            onHover={handleHover}
            hoverId={hoverIds[chartId] ?? null}
            isAnyShapeEnabled={isAnyShapeEnabled}
            {...props}
          />
        );
      case ShapeType.ELLIOTT_IMPULSE_12345:
        return (
          <ElliottImpulseC
            ref={handleShapeRef(ShapeType.ELLIOTT_IMPULSE_12345)}
            {...commonProps}
            {...props}
            type={ShapeType.ELLIOTT_IMPULSE_12345}
            snap={false}
            snapTo={snapToHl}
            elliottImpulses={inject(chartShapes?.ElliottImpulse12345, chartId, ShapeType.ELLIOTT_IMPULSE_12345)}
          />
        );
      case ShapeType.MEASURE: {
        const measureBgColor = MeasureBgColor(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          chartShapes?.Measure?.[0] as { end?: { yValue?: number }; start?: { yValue?: number } } | null | undefined,
        );
        const solidBgColor = solidColor(measureBgColor!);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const MeasureC = Measure as unknown as GenericComp;
        return (
          <MeasureC
            ref={handleShapeRef(ShapeType.MEASURE)}
            onBrush={(event: React.MouseEvent, newShapes: unknown[]) =>
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              onDrawComplete(event, newShapes as RenderNode[], type)
            }
            onStart={(event: React.MouseEvent, react: unknown, moreProps: MoreProps) =>
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              onStart(event, react as [number, number] | [number, number][], moreProps, type)
            }
            enabled={shapeTypeToDraw === ShapeType.MEASURE}
            chartId={chartId}
            completedRect={chartShapes?.Measure}
            timeFrame={selectedCanvasId?.timeframe}
            sharedState={sharedState}
            setSharedState={setSharedState}
            apperance={{
              strokeStyle: solidBgColor,
              fillStyle: measureBgColor,
              strokeDashArray: 'Solid',
              textFill: '#fff',
              fontFamily: 'Arial',
              fontSize: 12,
              fontWeight: 'normal',
              fontStyle: 'normal',
              bgFill: solidBgColor,
            }}
          />
        );
      }
      case ShapeType.RECTANGLE:
        return (
          <RectangleC
            ref={handleShapeRef(ShapeType.RECTANGLE)}
            {...commonProps}
            type="LINE"
            snap={false}
            snapTo={snapToHl}
            trends={inject(chartShapes?.Rectangle, chartId, ShapeType.RECTANGLE)}
            {...props}
          />
        );
      case ShapeType.FIXED_RANGE_VOLUME_PROFILE:
        return (
          <FixedRangeVolumeProfileC
            ref={handleShapeRef(ShapeType.FIXED_RANGE_VOLUME_PROFILE)}
            {...commonProps}
            type="LINE"
            snap={false}
            snapTo={snapToHl}
            trends={inject(
              chartShapes?.[ShapeType.FIXED_RANGE_VOLUME_PROFILE],
              chartId,
              ShapeType.FIXED_RANGE_VOLUME_PROFILE,
            )}
            {...props}
          />
        );
      case ShapeType.ANCHORED_VOLUME_PROFILE:
        return (
          <AnchoredVolumeProfileC
            ref={handleShapeRef(ShapeType.ANCHORED_VOLUME_PROFILE)}
            {...commonProps}
            snap={false}
            snapTo={snapToHl}
            trends={inject(
              chartShapes?.[ShapeType.ANCHORED_VOLUME_PROFILE],
              chartId,
              ShapeType.ANCHORED_VOLUME_PROFILE,
            )}
            {...props}
          />
        );
      case ShapeType.TRIANGLE:
        return (
          <TriangleC
            ref={handleShapeRef(ShapeType.TRIANGLE)}
            {...commonProps}
            channels={inject(chartShapes?.Triangle, chartId, ShapeType.TRIANGLE)}
            {...props}
          />
        );
      case ShapeType.PATH:
        return (
          <PathImpulseC
            ref={handleShapeRef(ShapeType.PATH)}
            {...commonProps}
            {...props}
            type={ShapeType.PATH}
            snap={false}
            snapTo={snapToHl}
            elliottImpulses={inject(chartShapes?.Path, chartId, ShapeType.PATH)}
            completePath={completePath}
          />
        );
      case ShapeType.ELLIOTT_CORRECTION_ABC:
        return (
          <ElliottImpulseC
            ref={handleShapeRef(ShapeType.ELLIOTT_CORRECTION_ABC)}
            {...commonProps}
            {...props}
            type={ShapeType.ELLIOTT_CORRECTION_ABC}
            snap={false}
            snapTo={snapToHl}
            elliottImpulses={inject(chartShapes?.ElliottCorrectionABC, chartId, ShapeType.ELLIOTT_CORRECTION_ABC)}
          />
        );
      case ShapeType.PARALLEL_CHANNEL:
        return (
          <FibChannelC
            ref={handleShapeRef(ShapeType.PARALLEL_CHANNEL)}
            {...commonProps}
            type={ShapeType.PARALLEL_CHANNEL}
            channels={inject(chartShapes?.ParallelChannel, chartId, ShapeType.PARALLEL_CHANNEL)}
            {...props}
          />
        );
      case ShapeType.XABCD_PATTERN:
        return (
          <ElliottImpulseC
            ref={handleShapeRef(ShapeType.XABCD_PATTERN)}
            {...commonProps}
            type={ShapeType.XABCD_PATTERN}
            snap={false}
            snapTo={snapToHl}
            elliottImpulses={inject(chartShapes?.XABCDPattern, chartId, ShapeType.XABCD_PATTERN)}
            {...props}
          />
        );
      case ShapeType.ORDER_LINE: {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const InteractiveOrderLineC = InteractiveOrderLine as unknown as GenericComp;
        return (
          <InteractiveOrderLineC
            enabled={shapeTypeToDraw === ShapeType.ORDER_LINE}
            isDark={isDarkMode}
            onDragComplete={(e: React.MouseEvent, list: unknown[], _moreProps: MoreProps, dragged: unknown) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              onDragComplete(e, list as RenderNode[], ShapeType.ORDER_LINE, dragged as boolean);
            }}
            onDelete={(
              _e: React.MouseEvent | null,
              yCoordinate: OrderLineRenderNode,
              _moreProps: MoreProps,
              type: 'Entry' | 'TP' | 'SL',
            ) => handleDeleteAlert(yCoordinate, type)}
            orderLineList={injectOrderLine(chartShapes?.[ShapeType.ORDER_LINE], chartId)}
            onDoubleClick={(_e: React.MouseEvent, _id: string, moreProps: MoreProps) => {
              onDoubleClick?.(_e, moreProps);
            }}
            setRatioDropdown={setRatioDropdown}
            lastTPSLByDirection={lastTPSLByDirection}
            onDeleteAll={handleDeleteAllOrderLines}
          />
        );
      }
      default:
        return null;
    }
  };

  // VertLine은 chartId_common에만 저장되므로 직접 참조
  const allVerticalLines = commonShapes?.[ShapeType.VERT_LINE] ?? [];

  return (
    <div>
      {renderShapeComponent(ShapeType.MEASURE, shapeTypeToDraw === ShapeType.MEASURE, {
        brush: chartShapes?.Measure,
      })}
      {renderShapeComponent(ShapeType.TREND_LINE, shapeTypeToDraw === ShapeType.TREND_LINE, {
        trends_3: chartShapes?.TrendLine,
      })}
      {renderShapeComponent(ShapeType.RAY, shapeTypeToDraw === ShapeType.RAY, {
        trends_1: chartShapes?.Ray,
      })}
      {renderShapeComponent(ShapeType.INFO_LINE, shapeTypeToDraw === ShapeType.INFO_LINE, {
        trends_1: chartShapes?.InfoLine,
      })}
      {renderShapeComponent(ShapeType.HORZ_LINE, shapeTypeToDraw === ShapeType.HORZ_LINE, {
        trends_1: chartShapes?.HorzLine,
      })}
      {renderShapeComponent(ShapeType.VERT_LINE, shapeTypeToDraw === ShapeType.VERT_LINE, {
        trends_1: allVerticalLines,
      })}
      {renderShapeComponent(ShapeType.PARALLEL_CHANNEL, shapeTypeToDraw === ShapeType.PARALLEL_CHANNEL, {
        parallelChannel: chartShapes?.ParallelChannel,
      })}
      {renderShapeComponent(ShapeType.FIB_RETRACEMENT, shapeTypeToDraw === ShapeType.FIB_RETRACEMENT, {
        retracements: chartShapes?.FibRetracement,
      })}
      {renderShapeComponent(ShapeType.FIB_CHANNEL, shapeTypeToDraw === ShapeType.FIB_CHANNEL, {
        channels: chartShapes?.FibChannel,
      })}
      {renderShapeComponent(ShapeType.GANN_BOX, shapeTypeToDraw === ShapeType.GANN_BOX, {
        fans: chartShapes?.GannBox,
      })}
      {renderShapeComponent(ShapeType.GANN_FAN, shapeTypeToDraw === ShapeType.GANN_FAN, {
        fans: chartShapes?.GannFan,
      })}
      {renderShapeComponent(ShapeType.ELLIOTT_IMPULSE_12345, shapeTypeToDraw === ShapeType.ELLIOTT_IMPULSE_12345, {
        elliottImpulses: chartShapes?.ElliottImpulse12345,
      })}
      {renderShapeComponent(ShapeType.ELLIOTT_CORRECTION_ABC, shapeTypeToDraw === ShapeType.ELLIOTT_CORRECTION_ABC, {
        elliottImpulses: chartShapes?.ElliottCorrectionABC,
      })}
      {renderShapeComponent(ShapeType.XABCD_PATTERN, shapeTypeToDraw === ShapeType.XABCD_PATTERN, {
        XABCDPattern: chartShapes?.XABCDPattern,
      })}
      {renderShapeComponent(ShapeType.RECTANGLE, shapeTypeToDraw === ShapeType.RECTANGLE, {
        trends_1: chartShapes?.Rectangle,
      })}
      {isCandleChart &&
        renderShapeComponent(
          ShapeType.FIXED_RANGE_VOLUME_PROFILE,
          shapeTypeToDraw === ShapeType.FIXED_RANGE_VOLUME_PROFILE,
          {
            trends_1: chartShapes?.[ShapeType.FIXED_RANGE_VOLUME_PROFILE],
          },
        )}
      {isCandleChart &&
        renderShapeComponent(ShapeType.ANCHORED_VOLUME_PROFILE, shapeTypeToDraw === ShapeType.ANCHORED_VOLUME_PROFILE, {
          trends_1: chartShapes?.[ShapeType.ANCHORED_VOLUME_PROFILE],
        })}
      {renderShapeComponent(ShapeType.TRIANGLE, shapeTypeToDraw === ShapeType.TRIANGLE, {
        trends_1: chartShapes?.Triangle,
      })}
      {renderShapeComponent(ShapeType.PATH, shapeTypeToDraw === ShapeType.PATH, {
        elliottImpulses: chartShapes?.Path,
      })}
      {renderShapeComponent(ShapeType.TEXT, shapeTypeToDraw === ShapeType.TEXT, {
        InteractiveText: chartShapes?.Text,
      })}
      {renderShapeComponent(ShapeType.ORDER_LINE, shapeTypeToDraw === ShapeType.ORDER_LINE, {
        orderLine: chartShapes?.OrderLine,
      })}
    </div>
  );
};

export default React.memo(ShapeRenderComponent);
